'use client';

import { useEffect, useRef } from 'react';
import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  OrthographicCamera,
  ShaderMaterial,
  Color,
  PlaneGeometry,
  Mesh,
  Vector3,
  Vector2,
  Raycaster,
  Plane,
  AmbientLight,
  WebGLRenderTarget,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { ColorGradeVignetteShader } from '@/lib/koi-pond-v3/postprocess/colorGrade';
import { makeMoonLensFlare } from '@/lib/koi-pond-v3/postprocess/moonLensFlare';
import { palette } from '@/lib/koi-pond-v3/palette';
import { makeCausticFloorMaterial } from '@/lib/koi-pond-v3/shaders/causticFloor';
import { makeSharedFogUniforms } from '@/lib/koi-pond-v3/shaders/depthFog';
import { makeMoon } from '@/lib/koi-pond-v3/assets/moon';
import { makeWaterSurfaceV3Material } from '@/lib/koi-pond-v3/shaders/waterSurfaceV3';
import { makeSkyDome } from '@/lib/koi-pond-v3/assets/skyDome';
import { makeKoiV3Geometry } from '@/lib/koi-pond-v3/geometry/koiV3';
import { makeKoiMaterialV3, KOI_VARIANTS } from '@/lib/koi-pond-v3/shaders/koiMaterialV3';
import { makeKoiSwimmer } from '@/lib/koi-pond-v3/behaviors/koiPathFollowing';
import { makeKoiConvergence } from '@/lib/koi-pond-v3/behaviors/koiConvergence';
import { makeWaterDisturbances } from '@/lib/koi-pond-v3/interaction/waterDisturbances';
import { makeSurfaceBreak } from '@/lib/koi-pond-v3/interaction/surfaceBreak';
import type { SurfaceBreakKoi } from '@/lib/koi-pond-v3/interaction/surfaceBreak';
import { makeLotusPads } from '@/lib/koi-pond-v3/assets/lotusPads';
import { makeStones } from '@/lib/koi-pond-v3/assets/stones';
import { makeFireflies } from '@/lib/koi-pond-v3/particles/fireflies';
import { makeFallingPetals } from '@/lib/koi-pond-v3/particles/fallingPetals';
import { makeMoonCloudDimming } from '@/lib/koi-pond-v3/behaviors/moonCloudDimming';
import { makeGhostKoi } from '@/lib/koi-pond-v3/assets/ghostKoi';
import { TIERS, detectTier, lowerTier } from '@/lib/koi-pond-v3/quality/tiers';
import { Spring2, expDamp } from '@/lib/koi-pond-v3/quality/damp';
import { makeDragonGate } from '@/lib/koi-pond-v3/interaction/dragonGate';
import { moonHit, DRAGON_GATE_DURATION } from '@/lib/koi-pond-v3/interaction/dragonGatePhases';

// Pre-Phase-7 scaffold. Subsequent tasks (T7.1 caustic floor, T7.2
// depth fog, T7.3 water surface, T7.4 moon, T7.5 reflection) mount
// their assets between the OrbitControls setup and the animate loop.
//
// Camera starts at the v2 vantage (above looking down). T7.3 will
// reposition to under-water looking up so the surface + moon can
// be visible from the viewer's POV.
//
// window.__pondV3 exposes scene/renderer/camera/controls for the
// same diag-script + probe pattern Phase 6 used on v2.

export interface PondSceneV3Props {
  /**
   * 'standalone' (default) — the /pond-v3 dev surface: the canvas fills the
   *   screen, OrbitControls let you fly around, pointer input binds to the
   *   canvas itself.
   * 'background' — mounted by BackgroundProvider behind the site content
   *   (T12.2 cutover). The canvas sits at z-index 0 / pointer-events:none, so
   *   pointer + scroll input is read from `window` instead (and clicks over
   *   real UI are ignored). OrbitControls are off; a slow camera drift keeps
   *   the scene alive without letting visitors fly the homepage background.
   */
  mode?: 'standalone' | 'background';
}

export function PondSceneV3({ mode = 'standalone' }: PondSceneV3Props = {}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;
    const isBackground = mode === 'background';
    // T12.2 — over the homepage, koi must see the cursor everywhere (so they
    // veer from it over empty water) but must NOT panic when the visitor is
    // reading the headline or aiming for a button, and a click on a real
    // control must NOT spawn a ripple. This selector is the "this is UI, leave
    // it alone" test, mirroring the Wave-G cursor-scare spec.
    const UI_SELECTOR = 'a, button, input, textarea, select, label, [role="button"], [data-koi-avoid]';
    const isUiTarget = (el: EventTarget | null) =>
      isBackground && el instanceof Element && !!el.closest(UI_SELECTOR);

    // T12.4 — reduced motion. On the production homepage a reduced-motion
    // visitor gets a beautiful settled STILL (no swim, no drift, no beats, no
    // GPU churn) instead of the animated scene. (Standalone /pond-v3 keeps
    // animating — it's a dev surface.)
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const staticStill = isBackground && prefersReduced;
    // Time the static still freezes at — a settled, composed frame.
    const REDUCED_FREEZE_T = 2.0;

    // Fluidity — device quality tier (premium / standard / lite). The audit
    // found the scene is GPU-fragment-bound, so the tier dials are mostly
    // fragment cost (DPR, DoF, MSAA, shader detail, SSS) plus koi count +
    // particle pools. `lite` is the old mobile path. A `?tier=` override is
    // honored for testing; a runtime warmup (below) can drop DoF + DPR if the
    // first frames overrun the 60fps budget.
    let tier = TIERS[detectTier()];

    const renderer = new WebGLRenderer({
      antialias: tier.antialias,
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, tier.dprCap));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(new Color(palette.deepWater.hex), 1);
    container.appendChild(renderer.domElement);

    const scene = new Scene();
    scene.background = new Color(palette.deepWater.hex);

    const camera = new PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.1,
      100,
    );
    // T7.6c — camera reframed. Moon is at (4, 5, -3.5) per T7.6c
    // moon-lowering decision. Camera at (0, 2.0, 7.0) tilts slightly
    // upward + slightly right via lookAt (1.5, 3.0, -2). Goal: moon
    // lands at upper-right rule-of-thirds intersection. FOV widened
    // to 50° (from 42°) for cinematic framing + breathing room for
    // both the moon AND its reflection on the water surface
    // (visible in lower-center where the moon's reflection path
    // crosses the surface y=1 plane).
    // T7.6g — lookAt lowered y=3.0 → y=2.0 to bring water surface
    // and the moon's reflection geometry into the visible viewport.
    // Reflection projection math: virtual moon (reflected across the
    // y=1 mirror plane) at (4, -3, -3.5); line from camera through
    // virtual moon hits water at ~(0.8, 1, 4.9). lookAt y=3.0 had
    // forward direction tilted up enough that 4.9z lay below frame.
    // y=2.0 makes forward more horizontal → reflection lands in lower
    // portion of frame; moon's y=5 elevation keeps it in upper portion.
    //
    // KOI-VISIBILITY RECOMPOSE (polish round): the y=2.0 grazing gaze
    // pushed the koi band (swimming ~1.5–2.5 units below the surface)
    // down to/below the bottom frame edge — all the Phase-8 koi craft
    // was framed out. Tilting the gaze DOWN (lookAt y 2.0 → 1.0, eye
    // raised 2.0 → 2.3) lifts the koi into the lower third AND steepens
    // the angle onto the water so fresnel drops and the surface turns
    // more transparent — we see INTO the pond instead of off a mirror.
    // The moon (y=5) rides toward the upper edge but stays in frame;
    // its reflection lane stays visible in the mid/lower water.
    // ZOOM-OUT RECOMPOSE: pulled the eye back (z 7.0 → 9.0, y 2.3 → 2.7)
    // so the full composition — pond extent, both foreground rocks, moon,
    // and starfield — sits in frame with margin instead of clipping at the
    // edges. Dolly back (not a wider FOV) keeps the rock proportions natural
    // and avoids edge stretching. Per-aspect framing is applied just below.
    // HERO-CONTRAST FIX (B+A blend, 2026-07-14): the koi-visibility recompose
    // (lookAt y 2.0 → 1.0) dropped the bright moon-reflection band behind the
    // hero subtitle, failing WCAG AA (~1.1:1). Nudge the gaze partway back
    // (y 1.0 → 1.4) so the bright band sits below the text; the CSS legibility
    // scrim in HeroContent carries the remaining human-visible margin. Keeps
    // most of the koi band in the lower third.
    const HERO_LOOK = new Vector3(1.0, 1.4, -2.0);
    camera.position.set(0, 2.7, 9.0);
    camera.lookAt(HERO_LOOK);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 2;
    controls.maxDistance = 20;
    controls.target.copy(HERO_LOOK);
    // T12.2 — no flying the homepage background; the scene is a window the
    // content sits in front of. Disabled here, replaced by a slow drift below.
    controls.enabled = !isBackground;

    // T12.3 — gentle camera drift (background mode only) so the "window" feels
    // alive without OrbitControls. ±2° pan / ±1.4° tilt on slow, non-harmonic
    // sines around the fixed lookAt — same approach as v2's drift.
    const driftTarget = controls.target.clone();
    let driftOffset = camera.position.clone().sub(driftTarget); // base eye→target
    let driftBaseY = camera.position.y;

    // Aspect-aware framing — there's no separate mobile canvas; one scene
    // adapts per aspect. Vertical FOV is fixed, so narrow/portrait viewports
    // crop the sides. Dolly the eye further back as the aspect narrows so the
    // full composition stays in frame on phones/tall windows too.
    const FRAME_BASE_OFFSET = driftOffset.clone(); // wide-aspect eye→target
    const FRAME_REF_ASPECT = 1.6;
    const applyFraming = (aspect: number) => {
      const f = Math.min(1.7, Math.max(1.0, FRAME_REF_ASPECT / aspect));
      camera.position.copy(driftTarget).addScaledVector(FRAME_BASE_OFFSET, f);
      camera.lookAt(driftTarget);
      driftOffset = camera.position.clone().sub(driftTarget);
      driftBaseY = camera.position.y;
    };
    applyFraming(camera.aspect);

    // Tick handlers — each Phase 7+ asset that needs per-frame uTime
    // advancement pushes a function here. Animate loop iterates.
    const tickHandlers: Array<(t: number) => void> = [];

    // T7.2 — shared depth-fog uniforms. Every v3 material that
    // composes the fog snippet reads these BY REFERENCE so tuning
    // density in one place propagates everywhere.
    const sharedFogUniforms = makeSharedFogUniforms();

    // T7.1 — caustic-lit pond floor. Temporary hardcoded moon
    // direction (upper-right of world); T7.4 will mount the actual
    // moon and overwrite this uniform via floorUniforms.uMoonWorldDir.value.copy().
    const moonDirInitial = new Vector3(0.6, 1.0, -0.3).normalize();
    const floorGeom = new PlaneGeometry(20, 14);
    floorGeom.rotateX(-Math.PI / 2);
    const { material: floorMat, uniforms: floorUniforms } = makeCausticFloorMaterial(
      moonDirInitial,
      sharedFogUniforms,
      tier.causticDetail,
    );
    const floor = new Mesh(floorGeom, floorMat);
    floor.position.set(0, -3, 0);
    scene.add(floor);
    tickHandlers.push((t) => { floorUniforms.uTime.value = t; });

    // T7.4 — moon disc + halo + DirectionalLight. Mounted AFTER the
    // floor so we can overwrite floorUniforms.uMoonWorldDir with the
    // actual moon position (replacing the T7.1 hardcoded placeholder).
    const moon = makeMoon();
    scene.add(moon.disc);
    scene.add(moon.halo);
    scene.add(moon.light);
    scene.add(moon.lightTarget);
    // T9.3 tuning — the moon disc + halo live on layer 1 so the DoF pass
    // (which renders layer 0) never blurs them. A celestial body shouldn't
    // be depth-of-field blurred, and T9.4's anamorphic flare needs a crisp
    // disc to anchor to. They're composited sharp over the DoF'd frame in
    // the animate loop. The DirectionalLight stays on layer 0 so it still
    // lights the koi/floor.
    const MOON_LAYER = 1;
    moon.disc.layers.set(MOON_LAYER);
    moon.halo.layers.set(MOON_LAYER);

    // T9.4 — anamorphic lens flare on the moon layer (composited sharp,
    // screen-aligned each frame so the streak stays horizontal).
    const moonFlare = makeMoonLensFlare(moon.moonGroupPos);
    moonFlare.mesh.layers.set(MOON_LAYER);
    scene.add(moonFlare.mesh);

    // Overwrite floor's moonDir with the actual moon direction.
    floorUniforms.uMoonWorldDir.value.copy(moon.moonDir);

    // T8.1b — ambient fill for koi materials. Without ambient light,
    // sides of koi facing away from the moon DirectionalLight render
    // pitch black. Real moonlit scenes have ambient sky luminance
    // (handled in shader for floor/water/sky via constant baselines,
    // but standard PBR materials like MeshStandardMaterial need an
    // AmbientLight). Cool-tinted to match the moonlight palette.
    const ambient = new AmbientLight(new Color(0x8aa0b8), 0.5);
    scene.add(ambient);

    // T7.6h — sky dome with hemispherical moon glow. Mounted at
    // renderOrder=-1 so it draws before everything else, serving as
    // the effective scene backdrop. Eliminates the "moon pasted on
    // pure-black void" feel by adding ambient sky luminance that
    // brightens near the moon and softens the horizon line.
    const sky = makeSkyDome(moon.moonDir);
    scene.add(sky.mesh);

    // T8.1 — koi geometry mount. Three koi sharing the SAME procedural
    // BufferGeometry (uploaded to GPU once) with different placeholder
    // materials per variant. T8.2 will replace these with proper SSS
    // materials; T8.3 will add bone-driven swim animation. For now
    // koi are static — visual verification target is "do they read
    // as fish-shaped, properly proportioned, in the right scene
    // composition?"
    const koiGeom = makeKoiV3Geometry({ quality: tier.koiQuality });
    type KoiSpec = {
      name: string;
      position: [number, number, number];
      rotationY: number;
      scale: number;
      bodyColor: number;
    };
    // The three composed resident koi (standard/lite). Premium adds more,
    // distributed around the lit band on a golden-angle spread; the swimmer's
    // soft bounds pull them onto-camera, so loose initial placement settles.
    const baseKoiSpecs: KoiSpec[] = [
      // Kohaku: foreground-right
      { name: 'kohaku', position: [1.6, -0.4, 1.5], rotationY: -0.7, scale: 1.5, bodyColor: 0xf4e8d4 },
      // Sanke: center
      { name: 'sanke', position: [0.0, -0.5, 0.5], rotationY: 0.2, scale: 1.4, bodyColor: 0xeadec6 },
      // Ogon: background-left
      { name: 'ogon', position: [-1.4, -0.6, -0.5], rotationY: 1.0, scale: 1.6, bodyColor: 0xc9a253 },
    ];
    const VARIANT_CYCLE = ['kohaku', 'sanke', 'ogon'];
    const koiSpecs: KoiSpec[] = [];
    for (let i = 0; i < tier.koiCount; i++) {
      if (i < baseKoiSpecs.length) { koiSpecs.push(baseKoiSpecs[i]); continue; }
      const a = i * 2.39996; // golden angle (radians) — even, non-repeating spread
      const r = 1.4 + (i % 3) * 0.7;
      koiSpecs.push({
        name: VARIANT_CYCLE[i % 3],
        position: [1.0 + Math.cos(a) * r, -0.4 - (i % 4) * 0.3, -0.5 + Math.sin(a) * r * 0.8],
        rotationY: a,
        scale: 1.3 + (i % 3) * 0.12,
        bodyColor: 0xf4e8d4,
      });
    }
    // ⚠ REVIEW FLAG — flip to false to restore all production cadences in
    // one line. true compresses the rare atmosphere beats so a reviewer
    // can watch each fire in one short sitting:
    //   depth excursions ~150s→~11s, petals 15–30s→3–5s, moon-dimming
    //   30–60s→6–9s, koi convergence 2–4min→7–10s.
    // MUST be false in production.
    const REVIEW_FAST_CADENCE = false;
    const REVIEW_EXCURSION_SPEEDUP = REVIEW_FAST_CADENCE ? 14 : 1;

    type KoiAgent = {
      koi: Mesh;
      uniforms: { uTime: { value: number }; uSwimTime: { value: number }; uSwimGain: { value: number }; uSwimSpeed: { value: number }; uMoonDim: { value: number }; uShimmer: { value: number } };
      swimmer: ReturnType<typeof makeKoiSwimmer>;
    };
    const koiAgents: KoiAgent[] = [];
    // T10.4 — occasional shared gathering point (every 2–4 min).
    const convergence = makeKoiConvergence(REVIEW_FAST_CADENCE);

    // T11.1 — cursor tracking for koi avoidance. NDC pointer + smoothed
    // speed; per koi we compare screen-space distance and (if close)
    // project the cursor to the koi's depth plane to steer it away.
    const pointerNDC = new Vector2(99, 99); // off-screen until first move
    const prevPointerNDC = new Vector2(99, 99);
    let pointerInside = false;
    // T12.2 — true while the cursor is over real UI (headline, CTA, nav). The
    // koi-scare (T11.1) is suppressed so fish don't panic at the reader.
    let pointerOverUI = false;
    let pointerSpeed = 0;
    let lastPointerT = -1;
    const avoidRay = new Raycaster();
    const avoidPlane = new Plane(new Vector3(0, 1, 0), 0);
    const cursorWorld = new Vector3();
    const koiNDC = new Vector3();
    const AVOID_RADIUS_NDC = 0.18; // ≈ 80–120px on a ~1300px-wide viewport
    // T11.5 — visitor-activity timestamp (wall-clock ms). Ambient surface
    // breaks bias up when the visitor has been idle ~30s. Stamped by pointer
    // + wheel/scroll handlers; sampled each frame.
    let lastActivityMs = performance.now();
    // Latest animation time (seconds), refreshed each frame so the scroll/
    // wheel event handlers can hand a current `t` to the surface-break trigger.
    let frameT = 0;
    // Dragon Gate — assigned below (once drawFrame exists); pumps a short rAF
    // for the beat when the scene is otherwise a reduced-motion still.
    let kickReducedBeat: () => void = () => {};
    // Per-frame delta (seconds), for frame-rate-independent damping. Clamped so
    // a tab-blur gap can't blow the springs up.
    let frameDt = 1 / 60;
    let lastDrawT = -1;
    const onPointerMove = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointerNDC.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      pointerInside = true;
      pointerOverUI = isUiTarget(e.target);
      // Dragon Gate — hover the moon: pointer cursor + a tiny bloom hint (only
      // until the egg fires; trigger sets the session flag so hasFired() stays
      // true and this stops touching the moon afterward).
      if (!dragonGate.hasFired()) {
        const mm = moon.moonGroupPos.clone().project(camera);
        const overMoon = moonHit(pointerNDC.x, pointerNDC.y, mm.x, mm.y, mm.z, 0.12);
        (isBackground ? document.body : renderer.domElement).style.cursor = overMoon ? 'pointer' : '';
        moon.setBloom(overMoon ? 0.12 : 0);
      }
      lastActivityMs = performance.now();
    };
    const onPointerLeave = () => { pointerInside = false; };
    // T12.2 — in background mode the canvas is behind content at
    // pointer-events:none, so its own DOM never sees the pointer. Bind to
    // `window` so the koi track the cursor across the whole page; in
    // standalone the canvas fills the screen so window == canvas anyway.
    const inputTarget: Window | HTMLElement = isBackground ? window : renderer.domElement;
    inputTarget.addEventListener('pointermove', onPointerMove as EventListener);
    inputTarget.addEventListener('pointerleave', onPointerLeave as EventListener);

    // T11.2 — water disturbance system (assigned after the water mounts).
    // Declared here so the koi tick can read startle points from it.
    let disturbances: ReturnType<typeof makeWaterDisturbances> | null = null;
    const clickPlane = new Plane(new Vector3(0, 1, 0), -1.0); // water y=1
    const clickWorld = new Vector3();
    // T11.3 cursor-wake state
    const wakeWorld = new Vector3();
    let lastWakeT = -1;
    const lastWakePos = new Vector2(9999, 9999); // (x, z)
    const onPointerDown = (e: PointerEvent) => {
      lastActivityMs = performance.now();
      const rect = renderer.domElement.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      // Dragon Gate — clicking the moon ALWAYS fires the beat, checked BEFORE the
      // UI/shield guard below. The hero content wrapper carries
      // data-koi-avoid="true" (a content-shield so koi don't scare over the
      // headline + hero clicks don't ripple), and the moon sits over that region
      // — so the shield was eating the moon click. The moon is a deliberate
      // easter egg and wins; it sits in empty upper hero space, never over a real
      // CTA/nav control, so this can't hijack an actionable click.
      if (!dragonGate.hasFired()) {
        const m = moon.moonGroupPos.clone().project(camera);
        if (moonHit(nx, ny, m.x, m.y, m.z, 0.12)) {
          dragonGate.trigger((performance.now() - startMs) / 1000);
          if (staticStill) kickReducedBeat();
          return; // consume the click — no water ripple behind the moon
        }
      }
      // T12.2 — a click on a real control (CTA, nav link, form field) must do
      // its job, not drop a ripple in the pond behind it.
      if (isUiTarget(e.target)) return;
      avoidRay.setFromCamera(new Vector2(nx, ny), camera);
      if (disturbances && avoidRay.ray.intersectPlane(clickPlane, clickWorld)) {
        disturbances.add(clickWorld.x, clickWorld.z, 1.0, true); // startles koi
      }
    };
    inputTarget.addEventListener('pointerdown', onPointerDown as EventListener);

    koiSpecs.forEach((spec, i) => {
      // T8.2 — koi SSS material: moonlit wrapped diffuse + back-lit
      // translucency (thin fins glow) + cool fresnel rim + per-variant
      // markings. T8.3 — each koi swims with its OWN phase + beat so the
      // three never undulate in lockstep.
      const variant = KOI_VARIANTS[spec.name] ?? KOI_VARIANTS.kohaku;
      const { material: mat, uniforms: koiUniforms } = makeKoiMaterialV3({
        baseKey: variant.baseKey,
        patternKey: variant.patternKey,
        patternStrength: variant.patternStrength,
        rimKey: variant.rimKey,
        groundCool: variant.groundCool,
        // `phase` persists (breaks lockstep). `speed` is only a frame-0 seed —
        // the animate loop overwrites uSwimSpeed with the swimmer's beat
        // (baseBeat-derived) every frame, so the real tail rate lives on
        // baseBeat in makeKoiSwimmer below, NOT here.
        swim: { phase: i * 2.1, speed: 1.0 + i * 0.15 },
        sss: tier.sss, // no subsurface scattering on the lite tier
        moonDir: moon.moonDir,
        fogUniforms: sharedFogUniforms,
      });
      // T8.5 — per-koi preferred depth band (mostly midwater, spread so
      // they occupy distinct depths; the deepest reads dimmer + bluer).
      // KOI-VISIBILITY RECOMPOSE: lifted the whole band shallower (was
      // -0.5 - i*0.32, spanning -0.5..-3.4) and compressed the spread
      // (-0.15 - i*0.18 → -0.15..-1.77) so koi sit closer to the surface
      // y=1, catching more moonlight + less depth-fog. They read as the
      // lit subject instead of dark shapes at the deep, blue end.
      const depthCenter = -0.15 - i * 0.18;
      const koi = new Mesh(koiGeom, mat);
      koi.position.set(spec.position[0], depthCenter, spec.position[2]);
      koi.scale.setScalar(spec.scale);
      scene.add(koi);

      // T8.4 — each koi wanders the pond on its own lazy path, banking
      // into turns. T8.5 adds preferred depth + occasional surface/floor
      // excursions. Feeds uSwimGain/uSwimSpeed so a faster koi flexes harder.
      const swimmer = makeKoiSwimmer({
        start: new Vector3(spec.position[0], depthCenter, spec.position[2]),
        heading: -spec.rotationY,
        // cruise translation speed — eased ~0.7x (was 0.32 + i*0.1) so the koi
        // glide rather than dart across the pond.
        baseSpeed: 0.22 + i * 0.07,
        // tail-beat rate per koi — halved (was 1.6 + i*0.3) because the tails
        // still read as buzzing/twitching; real koi sway slowly. This is the
        // master temporal rate: state.beat = baseBeat*(0.85+0.4*norm) feeds
        // uSwimSpeed, and the whole undulation (body + tail are one traveling
        // wave) slows in sync. Amplitude (uSwimAmp) is unchanged — only the
        // frequency drops.
        baseBeat: 0.8 + i * 0.15,
        seed: 1.3 + i * 2.6,
        // T9 composition steer — bias the wander centre toward the moonlit
        // glint band (right/centre, moon side) so the koi cross the lit
        // water and read as the lit subject rather than a silhouette
        // against it. Was { cx: 0.3, cz: 0.7, rx: 2.3, rz: 1.7 }.
        bounds: { cx: 1.2, cz: 0.9, rx: 2.0, rz: 1.5 },
        depthCenter,
        excursionSpeedup: REVIEW_EXCURSION_SPEEDUP,
      });
      koiAgents.push({ koi, uniforms: koiUniforms, swimmer });
    });

    // T8.6 — drive the whole school in ONE tick so each koi steers off
    // the others' positions. Snapshot planar positions first (no update
    // order bias), then update each swimmer with its neighbours.
    tickHandlers.push((t) => {
      const snap = koiAgents.map((a) => ({
        x: a.swimmer.state.position.x,
        z: a.swimmer.state.position.z,
      }));
      const pull = convergence.update(t);
      const conv = pull.strength > 0 ? pull : null;

      // pointer speed (NDC/sec), smoothed
      if (lastPointerT < 0) lastPointerT = t;
      const pdt = Math.max(1e-3, t - lastPointerT);
      lastPointerT = t;
      if (pointerInside) {
        const inst = pointerNDC.distanceTo(prevPointerNDC) / pdt;
        pointerSpeed = pointerSpeed * 0.7 + inst * 0.3;
      } else {
        pointerSpeed = 0;
      }
      prevPointerNDC.copy(pointerNDC);

      koiAgents.forEach((a, idx) => {
        const neighbors = snap.filter((_, j) => j !== idx);

        // T11.1 — cursor avoidance: if the cursor is within the screen-
        // space radius of this koi, steer it away from the cursor's point
        // projected onto the koi's depth plane; scale by cursor speed.
        let avoid: { x: number; z: number; strength: number } | null = null;
        if (pointerInside && !pointerOverUI) {
          koiNDC.copy(a.koi.position).project(camera);
          const d = Math.hypot(koiNDC.x - pointerNDC.x, koiNDC.y - pointerNDC.y);
          if (koiNDC.z < 1 && d < AVOID_RADIUS_NDC) {
            avoidPlane.constant = -a.koi.position.y; // plane y = koi depth
            avoidRay.setFromCamera(pointerNDC, camera);
            if (avoidRay.ray.intersectPlane(avoidPlane, cursorWorld)) {
              const prox = 1 - d / AVOID_RADIUS_NDC;
              const strength = prox * (0.5 + Math.min(3.0, pointerSpeed * 2.5));
              avoid = { x: cursorWorld.x, z: cursorWorld.z, strength };
            }
          }
        }

        // T11.2 — a click ripple nearby startles the koi too; take whichever
        // of cursor-avoid / ripple-shock is stronger.
        if (disturbances) {
          const shock = disturbances.startleAt(a.koi.position.x, a.koi.position.z);
          if (shock && (!avoid || shock.strength > avoid.strength)) avoid = shock;
        }

        const st = a.swimmer.update(t, neighbors, conv, avoid);
        a.koi.position.copy(st.position);
        a.koi.quaternion.copy(st.quaternion);
        a.uniforms.uTime.value = t;
        a.uniforms.uSwimTime.value = t;
        a.uniforms.uSwimGain.value = st.gain;
        a.uniforms.uSwimSpeed.value = st.beat;
      });
    });

    // T7.3 — water surface plane at y=1 (above floor at y=-3, below
    // camera at y=2.8). 20x14 footprint matches floor; 64x48
    // segments give the per-vertex normal sampling enough resolution
    // for the displaced surface to read as continuous curves at
    // camera distance, not facets. renderOrder=1 ensures water draws
    // AFTER floor — alpha blending composites correctly.
    //
    // T7.5 — the same waterSurface material handles moon reflection
    // via uMoonWorldPos uniform.
    const waterGeom = new PlaneGeometry(20, 14, 64, 48);
    waterGeom.rotateX(-Math.PI / 2);
    const { material: waterMat, uniforms: waterUniforms } = makeWaterSurfaceV3Material(sharedFogUniforms);
    const water = new Mesh(waterGeom, waterMat);
    water.position.set(0, 1.0, 0);
    water.renderOrder = 1;
    scene.add(water);
    // T11.2 — disturbance system now that the water uniforms exist.
    disturbances = makeWaterDisturbances(waterUniforms);
    scene.add(disturbances.object);
    tickHandlers.push((t) => disturbances!.update(t));

    // T11.5 + T11.6 — surface-break: the signature "koi breaks the moon's
    // reflection" beat. Modeled as a LOCAL cluster of disturbance ripples at
    // the surfacing point (owner steer: a local break reads as the koi; a
    // global flicker reads as a lighting bug). T11.5 fires ambiently when a
    // koi rises into the moon's reflection lane (biased toward fresh-load +
    // idle); T11.6 fires when the visitor scrolls past the hero.
    const surfaceBreak = makeSurfaceBreak({
      add: (x, z, strength, startle, size) => disturbances!.add(x, z, strength, startle, size),
      moonXZ: new Vector2(moon.moonGroupPos.x, moon.moonGroupPos.z),
      getViewerXZ: () => new Vector2(camera.position.x, camera.position.z),
      fastCadence: REVIEW_FAST_CADENCE,
    });
    // Live view of the school for the controller (positions read each frame;
    // urgeSurface lets T11.6 pull the designated koi up).
    const surfaceBreakKoi: SurfaceBreakKoi[] = koiAgents.map((a) => ({
      pos: a.koi.position,
      urgeSurface: (t, dur) => a.swimmer.urgeSurface(t, dur),
    }));
    tickHandlers.push((t) => {
      frameT = t;
      const idle = (performance.now() - lastActivityMs) / 1000 > 30;
      surfaceBreak.update(t, surfaceBreakKoi, { idle, sinceLoad: t });
    });

    // T11.6 — scroll wiring. On the homepage (post-cutover) the hero-exit is
    // driven by scrollY; on the standalone /pond-v3 dev surface there's no
    // scroll container, so we ALSO accept a wheel gesture as a stand-in so the
    // beat is testable here. Either source crossing the hero threshold fires
    // one scroll break; scrolling/wheeling back to the top re-arms it.
    let scrollAccum = 0;
    let heroExited = false;
    const HERO_EXIT = 600; // px past hero before the designated koi breaks
    const maybeScrollBreak = () => {
      if (!heroExited && scrollAccum > HERO_EXIT) {
        heroExited = true;
        surfaceBreak.triggerScrollBreak(frameT, surfaceBreakKoi);
      } else if (scrollAccum < 60) {
        heroExited = false; // returned to top → allow another break
      }
    };
    const onWheel = (e: WheelEvent) => {
      lastActivityMs = performance.now();
      scrollAccum = Math.max(0, scrollAccum + e.deltaY);
      maybeScrollBreak();
    };
    const onScroll = () => {
      lastActivityMs = performance.now();
      scrollAccum = window.scrollY;
      maybeScrollBreak();
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    // T11.3 — cursor wake: while the cursor moves over the water, drop
    // faint, small, non-startling ripples along its path. Throttled by
    // time + distance so it's a sparse trailing wake, not a pool flood.
    // (pointerSpeed is refreshed by the koi tick earlier each frame.)
    tickHandlers.push((t) => {
      if (!disturbances || !pointerInside || pointerOverUI || pointerSpeed < 0.05) return;
      avoidRay.setFromCamera(pointerNDC, camera);
      if (!avoidRay.ray.intersectPlane(clickPlane, wakeWorld)) return;
      const moved = Math.hypot(wakeWorld.x - lastWakePos.x, wakeWorld.z - lastWakePos.y);
      if (t - lastWakeT > 0.14 && (moved > 0.4 || lastWakeT < 0)) {
        disturbances.addWake(wakeWorld.x, wakeWorld.z); // faint, capped to 4, age-tapered
        lastWakeT = t;
        lastWakePos.set(wakeWorld.x, wakeWorld.z);
      }
    });

    // T11.4 — cursor lens, now frame-rate-independent (fluidity pass). The lens
    // strength eases via dt-based exponential damping (same time constant at
    // 60/120/144Hz — was a per-frame ×0.06 lerp that ran ~2× fast at 120Hz),
    // and uCursorPos follows the cursor through a critically-damped spring so
    // fast pointer jerks produce a smooth momentum-y bend, not a snap.
    let cursorLensAmt = 0;
    const cursorSpring = new Spring2(14);
    let cursorSpringInit = false;
    tickHandlers.push(() => {
      let on = false;
      if (pointerInside && !pointerOverUI) {
        avoidRay.setFromCamera(pointerNDC, camera);
        if (avoidRay.ray.intersectPlane(clickPlane, clickWorld)) {
          on = true;
          if (!cursorSpringInit) { cursorSpring.set(clickWorld.x, clickWorld.z); cursorSpringInit = true; }
          const p = cursorSpring.update(clickWorld.x, clickWorld.z, frameDt);
          waterUniforms.uCursorPos.value.set(p.x, p.y);
        }
      }
      cursorLensAmt = expDamp(cursorLensAmt, on ? 1 : 0, 3.7, frameDt);
      waterUniforms.uCursorAmt.value = cursorLensAmt;
    });

    // Initialize moon position uniform now that moon is mounted.
    waterUniforms.uMoonWorldPos.value.copy(moon.moonGroupPos);
    tickHandlers.push((t) => {
      waterUniforms.uTime.value = t;
      waterUniforms.uCameraPos.value.copy(camera.position);
      waterUniforms.uResolution.value.set(
        window.innerWidth * renderer.getPixelRatio(),
        window.innerHeight * renderer.getPixelRatio(),
      );
    });

    // T9.1 — lotus pads floating on the water surface (y=1), asymmetric,
    // with one foreground pad carrying a closed bud. Near-silhouette dark
    // material + gentle bob/drift.
    const lotus = makeLotusPads(moon.moonDir, sharedFogUniforms);
    scene.add(lotus.object);
    tickHandlers.push((t) => lotus.update(t));

    // T9.2 — weathered stones at the foreground corners (static anchors).
    const stones = makeStones(moon.moonDir, sharedFogUniforms);
    scene.add(stones.object);

    // T10.1 — fireflies drifting above the water (layer 0 → DoF turns
    // foreground ones into soft bokeh). Camera-aware for billboarding.
    const fireflies = makeFireflies(tier.fireflies);
    scene.add(fireflies.object);
    tickHandlers.push((t) => fireflies.update(t, camera));

    // T10.2 — falling petals/leaves: bounded pools, ripple-on-touchdown,
    // then floating drift that fades. Spawns 1 every 15–30s.
    const petals = makeFallingPetals(REVIEW_FAST_CADENCE);
    scene.add(petals.object);
    tickHandlers.push((t) => petals.update(t));

    // T12.1 — rare luminous ghost koi. Materializes out of the dark water,
    // glides across, dissolves; never interacts with cursor or other koi.
    const ghostKoi = makeGhostKoi({
      geometry: koiGeom,
      moonDir: moon.moonDir,
      fogUniforms: sharedFogUniforms,
      fastCadence: REVIEW_FAST_CADENCE,
      sss: tier.sss,
    });
    scene.add(ghostKoi.object);
    tickHandlers.push((t) => ghostKoi.update(t));

    // ── Dragon Gate — the moon-click signature moment (one per session) ──
    const dragonGate = makeDragonGate({
      camera,
      moonPos: moon.moonGroupPos,
      setMoonBloom: (v) => moon.setBloom(v),
      water: waterUniforms,
      // shock originates on the water roughly below the moon (tuned in loop)
      moonReflXZ: new Vector2(moon.moonGroupPos.x * 0.4, moon.moonGroupPos.z * 0.4),
      setCausticFlare: (v) => { floorUniforms.uFlare.value = v; },
      setColorFlash: (v) => { ColorGradeVignetteShader.uniforms.uFlash.value = v; },
      scatterFireflies: (x, z, s) => fireflies.scatter(x, z, s),
      koi: koiAgents.map((a) => ({ position: a.koi.position, uniforms: a.uniforms, swimmer: a.swimmer, koiMesh: a.koi, baseScale: a.koi.scale.x })),
      triggerReflectionBreak: (t) => surfaceBreak.triggerScrollBreak(t, surfaceBreakKoi),
      reduced: staticStill,
    });
    tickHandlers.push((t) => dragonGate.update(t));

    // T10.3 — moon cloud-dimming. One shared 0.7–1.0 factor eases the moon
    // disc/halo/flare, its water reflection, and the koi down together
    // every 30–60s, as if an unseen cloud crossed the moon.
    const moonClouds = makeMoonCloudDimming(REVIEW_FAST_CADENCE);
    tickHandlers.push((t) => {
      const dim = moonClouds.update(t);
      moon.setDim(dim);
      moonFlare.setDim(dim);
      waterUniforms.uMoonDim.value = dim;
      for (const a of koiAgents) a.uniforms.uMoonDim.value = dim;
    });

    // T12.6 — hidden developer trail. A greeting for anyone who opens the
    // console: the homepage IS the portfolio piece, so reward the curious dev
    // with a pointer to how it's built. (Paired with the footer credit.)
    // eslint-disable-next-line no-console
    console.log(
      '%c🌙 koi pond v3 %c— a real-time WebGL scene: caustics, depth-fogged water,\na moon reflection that ripples, SSS koi, and a few secrets. Built by Alejandro.\nPoke around: window.__pondV3 — try __pondV3.breakNow(). Source: github.com/alemorale7777/alejandro-landing',
      'color:#f5ebd2;font-weight:700',
      'color:#a8c0d4',
    );

    // Expose for diag scripts + probe extension (parallel to v2's __pondV2).
    (window as { __pondV3?: unknown }).__pondV3 = {
      scene,
      renderer,
      camera,
      controls,
      tickHandlers,
      // T11.6 — exposed so a reviewer can fire the scroll break deterministically
      // (no need to wheel exactly past the threshold): __pondV3.breakNow().
      surfaceBreak,
      breakNow: () => surfaceBreak.triggerScrollBreak(frameT, surfaceBreakKoi),
      // Dragon Gate — fire the moon-click beat deterministically (probe/owner).
      fireDragonGate: () => {
        dragonGate.trigger((performance.now() - startMs) / 1000);
        if (staticStill) kickReducedBeat();
      },
      // Fluidity — quality summary for the gate / owner A/B (tier after any
      // runtime warmup relief). `bokeh` is defined just below; called post-init.
      getQuality: () => ({
        tier: tier.id,
        dpr: renderer.getPixelRatio(),
        dof: bokeh ? bokeh.enabled : false,
        composerSamples: tier.composerSamples,
        koi: koiAgents.length,
      }),
    };

    // T9.3 — post-processing pipeline via EffectComposer (replaces the
    // direct renderer.render). RenderPass → BokehPass (depth of field) →
    // color-grade + vignette ShaderPass (final, renders to screen).
    //
    // ⚠ DELIBERATE — DO NOT "fix" by re-adding OutputPass or moving the
    // grade to linear. In this three build (0.184) the composer's
    // RenderPass target already carries sRGB, so an OutputPass
    // double-encodes and lifts the near-black sky to teal-blue (diagnosed
    // by isolating the chain). Dropping OutputPass reproduces the approved
    // exposure exactly; the grade INTENTIONALLY runs in sRGB space — fine,
    // and more perceptually even, for a stylistic split-tone.
    //
    // DoF: focus ≈ the midground koi distance so the foreground stones/
    // pads and the far water/horizon go soft while the koi stay sharp.
    // aperture/maxblur kept small — restrained, cinematic, not a toy blur.
    // Premium gets a multisampled composer target (MSAA on the water/scene
    // edges through the post chain); standard/lite use the default target.
    let composerRT: WebGLRenderTarget | undefined;
    if (tier.composerSamples > 0) {
      const dbs = new Vector2();
      renderer.getDrawingBufferSize(dbs);
      composerRT = new WebGLRenderTarget(dbs.x, dbs.y, { samples: tier.composerSamples });
    }
    const composer = new EffectComposer(renderer, composerRT);
    composer.setPixelRatio(renderer.getPixelRatio());
    composer.setSize(window.innerWidth, window.innerHeight);
    composer.addPass(new RenderPass(scene, camera));
    // aperture 0.0007 (was 0.0009) widens the in-focus band a touch so a
    // koi swimming from midground toward the foreground stays sharp longer
    // before it softens.
    // DoF is the dominant GPU cost; the lite tier skips it (scene renders
    // all-sharp there, which reads fine at phone size). The runtime warmup can
    // also drop it live if frames overrun the budget.
    let bokeh: BokehPass | null = null;
    if (tier.dof) {
      bokeh = new BokehPass(scene, camera, { focus: 6.5, aperture: 0.0007, maxblur: 0.006 });
      composer.addPass(bokeh);
    }
    composer.addPass(new ShaderPass(ColorGradeVignetteShader));

    // HERO-CONTRAST FIX (A, scene-side — 2026-07-14). A CSS scrim is invisible
    // to the gl.readPixels contrast gate (it measures hero text vs the raw
    // framebuffer), so the legibility dim must be baked into the scene. This is
    // a soft dark ellipse over the upper-center hero-text region, drawn LAST in
    // drawFrame (after the sharp moon overlay) so it darkens even the bright
    // moon that sits behind the H1. Fullscreen NDC quad + ortho cam, so it needs
    // no per-frame billboarding and is independent of DoF/color-grade.
    const heroDimScene = new Scene();
    const heroDimCam = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const heroDimMat = new ShaderMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: { uStrength: { value: 0.72 } },
      vertexShader:
        'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader:
        'varying vec2 vUv; uniform float uStrength;' +
        'void main() {' +
        '  vec2 d = (vUv - vec2(0.5, 0.58)) / vec2(0.58, 0.40);' +
        '  float a = uStrength * (1.0 - smoothstep(0.72, 1.30, length(d)));' +
        '  gl_FragColor = vec4(0.016, 0.031, 0.047, a);' +
        '}',
    });
    const heroDimQuad = new Mesh(new PlaneGeometry(2, 2), heroDimMat);
    heroDimQuad.frustumCulled = false;
    heroDimScene.add(heroDimQuad);

    const startMs = performance.now();
    let rafId = 0;
    // One full frame at simulated time `timeSec`. Factored out so the
    // reduced-motion still (T12.4) and resize can draw a single frame without
    // running the animation loop.
    const drawFrame = (timeSec: number) => {
      frameDt = lastDrawT < 0 ? 1 / 60 : Math.min(0.1, timeSec - lastDrawT);
      lastDrawT = timeSec;
      for (const tick of tickHandlers) tick(timeSec);
      if (isBackground && !staticStill) {
        // T12.3 — slow non-harmonic pan/tilt around the fixed lookAt.
        const pan = (Math.PI / 180) * 2.0 * Math.sin((timeSec / 41) * Math.PI * 2);
        const tilt = (Math.PI / 180) * 1.4 * Math.sin((timeSec / 53) * Math.PI * 2);
        const cosP = Math.cos(pan);
        const sinP = Math.sin(pan);
        const ox = driftOffset.x * cosP - driftOffset.z * sinP;
        const oz = driftOffset.x * sinP + driftOffset.z * cosP;
        camera.position.set(
          driftTarget.x + ox,
          driftBaseY + Math.sin(tilt) * driftOffset.length() * 0.05,
          driftTarget.z + oz,
        );
        camera.lookAt(driftTarget);
      } else if (!isBackground) {
        controls.update();
      }
      // DoF'd scene (layer 0 only — moon excluded), then composite the
      // sharp moon (layer 1) on top so it isn't depth-of-field blurred.
      camera.layers.set(0);
      composer.render();
      // Overlay pass: null the background first — otherwise three repaints
      // scene.background over the whole framebuffer and erases the
      // composited scene before the moon draws.
      renderer.autoClear = false;
      const savedBg = scene.background;
      scene.background = null;
      renderer.clearDepth();
      moonFlare.update(camera); // keep the streak horizontal on screen
      camera.layers.set(1);
      renderer.render(scene, camera);
      scene.background = savedBg;
      // Hero-text legibility dim — drawn last so it darkens the moon behind the
      // text (the scene-side half of the B+A contrast fix). See setup above.
      renderer.render(heroDimScene, heroDimCam);
      camera.layers.set(0);
      renderer.autoClear = true;
    };
    // Runtime warmup relief: sample the first frames (skipping shader-compile
    // spikes); if the median overruns the 60fps budget, drop the proven
    // bottleneck — DoF off + DPR down a notch — without a costly full remount.
    const applyRelief = () => {
      if (bokeh) bokeh.enabled = false;
      const pr = Math.max(1, renderer.getPixelRatio() - 0.5);
      renderer.setPixelRatio(pr);
      composer.setPixelRatio(pr);
      renderer.setSize(window.innerWidth, window.innerHeight);
      composer.setSize(window.innerWidth, window.innerHeight);
      tier = TIERS[lowerTier(tier.id)]; // bookkeeping for __pondV3.getQuality
    };
    // A1 — pause the rAF loop when the tab is hidden (visibilitychange). Also
    // wires an IntersectionObserver, but note the background container is
    // position:fixed inset:0, so on the homepage it is ALWAYS intersecting —
    // the off-screen path never fires there (a persistent fixed background
    // can't be scrolled away). Kept for any future non-fixed mount; harmless
    // as a no-op. The real scroll-time/lag protection is the adaptive relief
    // below (drops DoF + DPR when sustained FPS is low). Hidden-tab rAF is
    // already throttled by browsers, but an explicit cancel also frees the
    // compositor.
    let onScreen = true;
    let docVisible = typeof document === 'undefined' ? true : !document.hidden;
    let loopRunning = false;
    const canRun = () => !staticStill && onScreen && docVisible;
    const startLoop = () => {
      if (loopRunning || !canRun()) return;
      loopRunning = true;
      lastRafMs = -1; // don't count the paused gap as a warmup frame delta
      frameHist.length = 0;
      rafId = requestAnimationFrame(animate);
    };
    const stopLoop = () => {
      loopRunning = false;
      cancelAnimationFrame(rafId);
    };
    // A2 — rolling frame-time monitor. Instead of one 15-frame check at load,
    // keep a sliding window and re-evaluate; relief can fire later (under
    // scroll/interaction), and only fires when sustained FPS is genuinely low.
    // Relief action is unchanged (DoF off + DPR down a notch) → no look change
    // on machines that hold frame rate. The sliding-window median plus a
    // one-time latch after it fires prevent thrashing.
    const FRAME_WINDOW = 45;      // ~0.75s at 60fps
    const RELIEF_MS = 22;         // sustained median worse than ~45fps
    let reliefApplied = false;
    let lastRafMs = -1;
    const frameHist: number[] = [];
    const maybeRelief = (nowMs: number) => {
      if (reliefApplied || staticStill || tier.id === 'lite') return;
      if (lastRafMs > 0) {
        frameHist.push(nowMs - lastRafMs);
        if (frameHist.length > FRAME_WINDOW) frameHist.shift();
        if (frameHist.length >= FRAME_WINDOW) {
          const s = [...frameHist].sort((a, b) => a - b);
          const med = s[s.length >> 1];
          if (med > RELIEF_MS) { applyRelief(); reliefApplied = true; }
        }
      }
      lastRafMs = nowMs;
    };
    const animate = () => {
      const nowMs = performance.now();
      drawFrame((nowMs - startMs) / 1000);
      maybeRelief(nowMs);
      if (loopRunning) rafId = requestAnimationFrame(animate);
    };
    // Expose a synchronous single-frame render for verification probes: a
    // gl.readPixels contrast check needs a draw + read in the SAME task, and
    // the renderer keeps preserveDrawingBuffer off (perf). Harmless in prod.
    (window as { __pondV3?: { renderFrame?: (t: number) => void } }).__pondV3!.renderFrame = drawFrame;
    if (staticStill) {
      // T12.4 — render the settled still once; no rAF loop → no motion, no
      // ongoing GPU cost. Resize re-renders a single frame (see onResize).
      drawFrame(REDUCED_FREEZE_T);
    } else {
      startLoop();
    }

    // Pause when the hero canvas scrolls out of view (background only — the
    // standalone /pond-v3 surface is full-screen so it has nothing to scroll).
    let io: IntersectionObserver | null = null;
    if (isBackground && typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver(
        (entries) => {
          onScreen = entries.some((e) => e.isIntersecting);
          if (canRun()) startLoop(); else stopLoop();
        },
        { threshold: 0 },
      );
      io.observe(container);
    }
    const onVisibility = () => {
      docVisible = !document.hidden;
      if (canRun()) startLoop(); else stopLoop();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Reduced-motion: the scene renders a single frozen frame, so a triggered
    // dragon-gate beat gets its own short rAF to animate, then refreezes.
    let beatRaf = 0;
    kickReducedBeat = () => {
      const beatEndsAt = performance.now() + (DRAGON_GATE_DURATION + 0.2) * 1000;
      const draw = () => {
        drawFrame((performance.now() - startMs) / 1000);
        if (performance.now() < beatEndsAt) beatRaf = requestAnimationFrame(draw);
        else { drawFrame(REDUCED_FREEZE_T); beatRaf = 0; }
      };
      if (!beatRaf) beatRaf = requestAnimationFrame(draw);
    };

    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h);
      composer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      // Re-frame for the new aspect in background mode (the homepage hero).
      // Interactive /pond-v3 keeps the user's orbit untouched on resize.
      if (isBackground) applyFraming(camera.aspect);
      if (staticStill) drawFrame(REDUCED_FREEZE_T); // T12.4 — keep the still sharp
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(beatRaf);
      io?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      loopRunning = false;
      window.removeEventListener('resize', onResize);
      inputTarget.removeEventListener('pointermove', onPointerMove as EventListener);
      inputTarget.removeEventListener('pointerleave', onPointerLeave as EventListener);
      inputTarget.removeEventListener('pointerdown', onPointerDown as EventListener);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
      controls.dispose();
      floorGeom.dispose();
      floorMat.dispose();
      waterGeom.dispose();
      waterMat.dispose();
      lotus.dispose();
      stones.dispose();
      fireflies.dispose();
      petals.dispose();
      ghostKoi.dispose();
      disturbances?.dispose();
      moonFlare.dispose();
      composer.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      if (renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
      delete (window as { __pondV3?: unknown }).__pondV3;
    };
  }, [mode]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        // Paint the deep-water color immediately (before the WebGL first frame)
        // so there's no flash of black during scene init.
        background: palette.deepWater.hex,
        // Behind the site content; never intercepts clicks (input is read from
        // window in background mode). Standalone keeps default hit-testing so
        // OrbitControls work.
        ...(mode === 'background' ? { pointerEvents: 'none' as const } : null),
      }}
    />
  );
}
